import { useNavigation } from '@react-navigation/native';
import { useBreakpointValueGS } from 'hooks/useBreakpointValueGS';
import { useEffect, useState } from 'react';
import { Dimensions, View } from 'react-native';
import theme from 'theme';
import { environmentUrl } from 'utils/environment';
import { calcSizeForDevice, calcSizeForTv, hourFormatterBySeconds } from 'utils/index';
import { Platform } from 'utils/platform';

import { useProgram } from './context';

import Button from 'components/Button';
import Card from 'components/Card';
import DropDown from 'components/DropDown';
import Grid from 'components/Grid';
import TvSeasonsMenu from 'components/TvSeasonsMenu/TvSeasonsMenu';
import IsLoading from 'components/isLoading';

type ProgramEpisodesProps = {
  seasons: CmsProgram['seasons'];
  episodes?: CmsApiMediaList & { percentageWatched?: number };
  setEpisode: (episode: CmsApiItem<CmsApiMediaList>) => void;
  setMediaId: (mediaId: string) => void;
  setSelected: (s: string) => void;
  selected?: string;
  actual: any;
  onEndReached?: () => void;
  isLoading?: boolean;
};

const ProgramEpisodes: React.FC<ProgramEpisodesProps> = ({
  actual,
  seasons,
  episodes,
  setMediaId,
  setEpisode,
  setSelected,
  onEndReached,
  isLoading,
}) => {
  const navigation = useNavigation();
  const [defaultSelected, setDefaultSelected] = useState(0);
  const [loading, setLoading] = useState(false);
  const program = useProgram();
  const baseUrl = environmentUrl();
  const { height } = Dimensions.get('window');

  useEffect(() => {
    setLoading(true);

    try {
      if (!!actual?.id && !!seasons.length) {
        const seasonIndex = Math.max(
          seasons.findIndex((s) => s?.id === actual.id),
          0
        );

        setDefaultSelected(seasonIndex);
      }
    } finally {
      setLoading(false);
    }
  }, [actual, seasons]);

  useEffect(() => {
    setLoading(false);
  }, [defaultSelected]);

  const numCols = useBreakpointValueGS({
    md: Platform.isAndroidTV ? 3 : 2,
    sm: 1,
    lg: 3,
    xl: Platform.isTV ? 3 : 4,
    base: 1,
  });

  const gap = useBreakpointValueGS({
    md: 8,
    sm: 8,
    lg: 16,
    xl: 16,
    base: 8,
  });

  const onTvMenuTabPress = (selectedValue: string) => {
    if (selectedValue !== actual?.id) {
      setSelected(selectedValue);
    }
  };

  const renderSeasonItem = ({ item }: any) => {
    return (
      <Button
        role="button"
        useTVFocus
        variant="solid"
        backgroundColor="transparent"
        style={{
          marginRight: calcSizeForTv(20),
          padding: calcSizeForTv(25),
          borderWidth: calcSizeForTv(3),
          borderBottomWidth: calcSizeForTv(3),
          borderColor: actual?._id === item?.id ? 'white' : 'transparent',
        }}
        textTransform="uppercase"
        textStyle={{
          fontSize: calcSizeForTv(20),
          lineHeight: calcSizeForTv(24),
          color: actual?.id === item?.id ? 'white' : '#7A8CFF',
          textShadowColor: 'rgba(7, 13, 49, 0.7)',
          textShadowOffset: { width: 1, height: 1 },
        }}
        onPress={() => onTvMenuTabPress(item.id)}>
        {item.name}
      </Button>
    );
  };

  const renderItem = (item: CmsApiItem<CmsApiMediaList> & { percentageWatched: number }) => (
    <Card
      type="midBoxed"
      title={item.title}
      duration={hourFormatterBySeconds(item.duration || 0)}
      description={item?.summary}
      percentageWatched={item.percentageWatched > 100 ? 100 : item.percentageWatched}
      uri={item.imageHorizontalSpalla?.url || item.imageHorizontal?.url}
      lines={{ title: 2, description: Platform.isTV ? 4 : 3 }}
      onPress={() => {
        const title = item?.title.replaceAll('-', '').replaceAll('/', '-').replaceAll(' ', '-');
        const usesSpalla = Boolean(item?.mediaSource || program.mediaSource);
        const idForUrl = usesSpalla ? item?.videoId : item?.mediaId;

        if (Platform.OS === 'web') {
          window.history.pushState(
            null,
            null as any,
            `${baseUrl}/${(program as any).id}/${actual.id}/${title}/${idForUrl}`
          );
        }

        setEpisode(item);
        setMediaId(idForUrl);
      }}
    />
  );

  if (!actual || !episodes?.items || loading) {
    return null;
  }

  return (
    <View
      style={{ gap: 16, backgroundColor: Platform.OS !== 'web' ? theme.colors.base.darkBlue : '' }}>
      {Platform.isTV ? (
        <TvSeasonsMenu items={seasons} renderItem={renderSeasonItem} />
      ) : (
        <View style={{ marginHorizontal: theme.space[2] }}>
          <DropDown
            items={
              seasons?.map((season) => ({
                id: season._id,
                value: season._id,
                label: season.name,
              })) || []
            }
            defaultSelectedItem={defaultSelected}
            onChangeSelectedItem={(item) => {
              navigation.setParams({ episodeName: '', seasonId: '', mediaId: '' } as any);
              setTimeout(() => setSelected(item.value), 500);
            }}
          />
        </View>
      )}

      {isLoading ? (
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            height: calcSizeForDevice(300, height / 1.3),
          }}>
          <IsLoading />
        </View>
      ) : (
        <Grid
          onEndReached={onEndReached}
          gap={gap}
          key={numCols}
          numColumns={numCols}
          renderItem={renderItem}
          data={episodes.items! as any}
        />
      )}
    </View>
  );
};

export default ProgramEpisodes;
